// Imports
import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { defaultStartPath } from 'Core/defaultValues';
import { HasPermission, ValidateVersion, GerVersionFromServer } from 'Core/security';
import { useDataDogViewTracking } from 'Core/hooks/useDataDogViewTracking';

// Containers
import AuthContainer from 'Core/containers/Auth';
import Header from 'Core/containers/Header';
import Sidebar from 'Core/containers/Sidebar';
import Catalogs from 'Core/catalogs';
import Loading from 'Components/Loading';
import Dtc from "Modules/.common/components/Dtc";
import SharedMedia from "Modules/.common/components/SharedMedia";
import ModalDownloadFiles from 'Modules/reports/views/main/components/reportsTab/.components/modalDownloadFiles';
import { detectBrowser } from 'Core/data/Helpers';
import {
    reportsOpenModalDownloadFiles,
    clearRedux,
    getResellerInfo, 
	catalogSetUrlsDownload,
	logout
} from 'Redux/actions';
import {NotificationManager} from "Components/Notifications";
import {downloadReportFile, resetDownloadReportFile} from "Modules/reports/views/main/components/reportsTab/redux/slice";

// Lazy load components with retry
const lazyWithRetry = (componentImport) =>
	lazy(async () => {
		const pageHasAlreadyBeenForceRefreshed = JSON.parse(
			window.localStorage.getItem(
				'page-has-been-force-refreshed'
			) || 'false'
		);

		try {
			const component = await componentImport();

			window.localStorage.setItem(
				'page-has-been-force-refreshed',
				'false'
			);

			return component;
		} catch (error) {
			if (!pageHasAlreadyBeenForceRefreshed) {
				// Assuming that the user is not on the latest version of the application.
				// Let's refresh the page immediately.
				window.localStorage.setItem(
					'page-has-been-force-refreshed',
					'true'
				);
				return window.location.reload();
			}

			// The page has already been reloaded
			// Assuming that user is already using the latest version of the application.
			// Let's let the application crash and raise the error.
			throw error;
		}
	});

//Modules
const RealtimeMaps = lazyWithRetry(() => import('./realtimeMaps'));
const MainComponent = lazyWithRetry(() => import('./main'));
const MaintenanceRouter = lazyWithRetry(() => import('./maintenance'));
const RouterIncidents = lazyWithRetry(() => import('./incidents'));
const ReportsRouter = lazyWithRetry(() => import('./reports'));
const BehaviorComponent = lazyWithRetry(() => import('./behavior'));
const FleetComponent = lazyWithRetry(() => import('./fleet'));
const Alerts = lazyWithRetry(() => import('./alerts'));
const VideoRouter = lazyWithRetry(() => import('./video'));
const Account = lazyWithRetry(() => import('./account'));
const Settings = lazyWithRetry(() => import('./settings'));
const Manage = lazyWithRetry(() => import('./manage'));
const DashboardView = lazyWithRetry(() => import('./dashboard'));
const RoutesRoutesView = lazyWithRetry(() => import('./routes'));
const FsmView = lazyWithRetry(() => import('./fsm'));
const DynamicFormsView = lazyWithRetry(() => import('./dynamicForms'));
const ChatsContainer = lazyWithRetry(() => import('./chat/views/chatsContainer'));
const WorkOrdersView = lazyWithRetry(() => import('Modules/workOrders'));
const RouterDriverPerformance = lazyWithRetry(() => import('./driverPerformance'));

//error page
const ErrorView = lazyWithRetry(() => import('./error'));

// Integragion modules
const ZonarELDView = lazyWithRetry(() =>  import('./integrations/zonarEld'));
const ApolloELDView = lazyWithRetry(() => import('./integrations/apolloEld'));
const FleetIOView = lazyWithRetry(() => import('./integrations/fleet-io'));
const CarIQView = lazyWithRetry(() => import('./integrations/cariq'));
const FieldSyncView = lazyWithRetry(() => import('./integrations/fieldSync'));
const ELDFleetView = lazyWithRetry(() => import('./integrations/eldFleet'));
const EVIRView = lazyWithRetry(() => import('./integrations/evir'));
const EVIRIBView = lazyWithRetry(() => import('./integrations/evir-ib'));
const EvirPxRouter = lazyWithRetry(() => import('./evirPx'));
const ZpassView = lazyWithRetry(() => import('./integrations/zpass'));

const MarketPlace = lazyWithRetry(() => import('./integrations/marketPlace'));
const MonarchIframe = lazyWithRetry(() => import('./integrations/monarch'));
const NetradyneView = lazyWithRetry(() => import('./integrations/netradyne'));
const CoachSSOView = lazyWithRetry(() => import('./integrations/coachSSO'));
const Template = lazyWithRetry(() => import('./.template'));

const ModulesRoutes = () => {
	// Track all route changes in DataDog RUM
	useDataDogViewTracking();

	const { messages } = useIntl();
	const dispatch = useDispatch();
	const location = useLocation()
	const navigate = useNavigate()

    const {
        loadingDownloadReportFile,
        urlReportFile
    } = useSelector(state => state.reportsTapSearchReportLogRedux);
	const user = useSelector(state => state.securityRedux.user);
    const preferenceNotification = useSelector((state) => state.catalogsRedux.preferenceNotification);
    const openUrlNotification = useSelector((state) => state.catalogsRedux.openUrlNotification);
	const idSubUserLogout = useSelector(state => state.catalogsRedux.idSubUserLogout);
	const isDeveloper  = useSelector(state => state.commomRedux.isDeveloper);
	const sidebarShow  = useSelector(state => state.commomRedux.sidebarShow);
	const isFullScreen  = useSelector(state => state.commomRedux.isFullScreen);
	const current_reseller = useSelector(state => state.resellerRedux.current_reseller);
	const type = useSelector(state => state.resellerRedux.type);
	let debug = Boolean(localStorage.getItem("debug")) === true;
    const browser = detectBrowser();

	const urlLinks = {
		"maps": "/app/realtimemaps/main",
		"vehiclestatus": "/app/fleet/main",
		"main": "/app/realtimemaps/main",
		"reports": "/app/reports/main",
		"dashboard": "/app/dashboard/main",
		"/app/fleet/main": "/app/fleet/main",
		"/app/realtimemaps/main": "/app/realtimemaps/main",
		"/app/reports/main": "/app/reports/main",
		"/app/dashboard/main": "/app/dashboard/main",
		"routes": "/app/dashboard/main",
		"/app/routes/main": "/app/routes/main",
	};

    useEffect(() => {
        if (preferenceNotification?.status) {
            NotificationManager.success(
                messages['accountInformation_success'],
                messages['success'],
                3000,
                null,
                null,
                'Success',
            );
        }
    }, [preferenceNotification]);

	useEffect(() => {
		if (idSubUserLogout) {
			dispatch(clearRedux("CATALOG_REDUX", { idSubUserLogout: null }))
			logout(navigate, current_reseller, type)
		}
	}, [idSubUserLogout])

	useEffect(() => {
		if (!current_reseller && user?.dealerId) {
			dispatch(getResellerInfo('id', user?.dealerId))
		}
	}, [user])

	// Check for new deployments on mount and every 5 minutes while the app is open
	useEffect(() => {
		GerVersionFromServer();
		const interval = setInterval(() => {
			GerVersionFromServer();
		}, 5 * 60 * 1000);
		return () => clearInterval(interval);
	}, []);

	const openDTC = useSelector(state => state.dtcRedux.open);

    useEffect(() => {
        if (urlReportFile){
            downloadFile(urlReportFile);
        }
    }, [loadingDownloadReportFile])

    useEffect(() => {
        if (openUrlNotification.url?.length > 0) {
            const link = openUrlNotification.url
            if (openUrlNotification.url?.length > 1) {
                dispatch(catalogSetUrlsDownload(link));
                dispatch(reportsOpenModalDownloadFiles(true));
            } else {
                const delay = (browser === 'Safari' || browser === 'Firefox') ? 1000 : 0;   
                // Add delay before downloading (in milliseconds) if browser is Safari or Firefox
                const timer = setTimeout(() => {
                    const newLink = Array.isArray(link) ? link[0]?.url : link
                    dispatch(downloadReportFile({ id: newLink }))
                }, delay);
                
                // Cleanup the timer on unmount or when effect re-runs
                return () => clearTimeout(timer);
            }
        }
    }, [openUrlNotification])

    const downloadFile = (url) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        dispatch(resetDownloadReportFile());
    }

	//set default path
	let defaultPath = defaultStartPath;
	const validateDefaultTab = (user, defaultPath) => {
		// validate if user has permission to enter to the default tab
		if (defaultPath == '/app/dashboard/main' || defaultPath == 'dashboard') {
			return HasPermission(user, 'db', [63])
		} else if (defaultPath == '/app/reports/main' || defaultPath == 'reports') {
			return HasPermission(user, 'nr', [21])
		} else if (defaultPath == '/app/fleet/main' || defaultPath == 'vehiclestatus') {
			// vehiclestatus
			return HasPermission(user, 'vstat', [103])
		} else {
			return HasPermission(user, defaultPath)
		}
	}

	//get information from user custom path
	if (user && user.activeTab) {
		defaultPath = user.activeTab
		defaultPath = (defaultPath == 'units') && 'vehiclestatus' || defaultPath;
		defaultPath = urlLinks[defaultPath] || '/app/realtimemaps/main'
		if (!validateDefaultTab(user, defaultPath)) {
			defaultPath = `/app/realtimemaps/main`;
		}
	}

	//redirect to path
	if (user && (location.pathname === '/' || location.pathname === '/app' || location.pathname === '/app/')) {
		return (<Navigate to={defaultPath} />);
	}

	return (
		<AuthContainer>
			{ValidateVersion("head")}
			<Catalogs user={user} />
			<div style={{ position: 'relative', zIndex: 9999 }}>
				{/* HEADER */}
				{
					!isFullScreen &&
					<Header />
				}
				{/* CONTAINER */}
				<div id='id_appContainer' className={cx("app-container", isFullScreen && 'full-screen-legacy')}>
					{/* SIDEBAR */}
					{
						!isFullScreen &&
						<Sidebar sidebarShow={sidebarShow} user={user} isDeveloper={isDeveloper} messages={messages} />
					}
					{/* PAGES */}
					<div className={cx('module-container', !sidebarShow && 'sidebar-min', isFullScreen && 'full-screen-legacy')}>
						<ModalDownloadFiles />
						<Suspense fallback={<Loading className="center-loading" />}>
							<Routes>
								<Route path='/' element={<Navigate to="/app/legacy/dashboard" />}></Route>
								<Route path='main' element={<MainComponent />} />
								<Route path='realtimemaps/*' element={<RealtimeMaps />} />
								<Route path='maintenance/*' element={<MaintenanceRouter />} />
								<Route path='incidents/*' element={<RouterIncidents />} />
								<Route path='fleet/*' element={<FleetComponent />} />
								<Route path='reports/*' element={<ReportsRouter />} />
								<Route path='behavior/*' element={<BehaviorComponent />} />
								<Route path='marketplace' element={<MarketPlace />} />
								<Route path='video/*' element={<VideoRouter />} />
								<Route path='alerts/*' element={<Alerts />} />
								<Route path='eldfleet' element={<ELDFleetView />} />
								<Route path='zonareld' element={<ZonarELDView />} />
								<Route path='zpass' element={<ZpassView />} />
								<Route path='evir' element={<EVIRView />} />
								<Route path='evir-ib' element={<EVIRIBView />} />
								<Route path='evir-px/*' element={<EvirPxRouter />} />
								<Route path='apolloeld' element={<ApolloELDView />} />
								<Route path='fleetio' element={<FleetIOView />} />
								<Route path='cariq' element={<CarIQView />} />
								<Route path='fieldSync' element={<FieldSyncView />} />
								<Route path='account/*' element={<Account />} />
								<Route path='settings/*' element={<Settings />} />
								<Route path='manage/*' element={<Manage />} />
								<Route path='dashboard/*' element={<DashboardView />} />
								<Route path='fsm/*' element={<FsmView />} />
								<Route path='routes/*' element={<RoutesRoutesView />} />
								<Route path='driverPerformance/*' element={<RouterDriverPerformance />} />
								<Route path='dynamicforms/*' element={<DynamicFormsView />} />
								<Route path='monarch/*' element={<MonarchIframe />} />
								<Route path='netradyne' element={<NetradyneView />} />
								<Route path='coaching' element={<CoachSSOView />} />
								<Route path='workorders/*' element={<WorkOrdersView />} />
								{
									debug &&
									<Route path='template/*' element={<Template />} />
								}
								<Route path='error' element={<ErrorView />} />
								<Route path="*" element={<Navigate to="/app/error" />} />
							</Routes>
						</Suspense>
						{openDTC && <Dtc />}
						<SharedMedia />
						<ChatsContainer />
					</div>
					{/* FOOTER */}
				</div>
			</div>
		</AuthContainer>
	);
}


export default ModulesRoutes
